/**
 * The arena, drawn.
 *
 * One job: turn an Arena into pixels on the plate - grid, apple, body, the
 * arrow the last move left, and the cell the safety layer refused. No text is
 * painted into the canvas: every word the plate shows lives in the DOM, where
 * the dictionary can reach it.
 */

import { head, STEPS, type Arena, type Direction, type Point } from "./arena";

export type FieldGeometry = {
  readonly cell: number;
  readonly left: number;
  readonly top: number;
};

/** A cell's top-left corner or its centre, in canvas pixels. */
export type FieldBox = {
  readonly x: number;
  readonly y: number;
};

/** The board inside the plate: square cells, centred, never touching the edge. */
export function fieldGeometry(input: {
  readonly width: number;
  readonly height: number;
  readonly size: number;
}): FieldGeometry {
  const padding = Math.max(18, Math.round(Math.min(input.width, input.height) * 0.06));

  const cell = Math.max(
    4,
    Math.floor(
      Math.min((input.width - padding * 2) / input.size, (input.height - padding * 2) / input.size),
    ),
  );

  const side = cell * input.size;

  return {
    cell,
    left: Math.round((input.width - side) / 2),
    top: Math.round((input.height - side) / 2),
  };
}

export type ArenaPaint = {
  readonly ctx: CanvasRenderingContext2D;
  readonly width: number;
  readonly height: number;
  readonly arena: Arena;
  /** The cell the model asked for when the guard refused it. */
  readonly refused: Point | null;
  /** The direction the last applied move went, drawn above the head. */
  readonly heading: Direction | null;
};

export function paintArena(input: ArenaPaint): void {
  const { ctx, width, height, arena } = input;
  const board = fieldGeometry({ width, height, size: arena.size });
  const side = board.cell * arena.size;

  ctx.clearRect(0, 0, width, height);
  ctx.fillStyle = "oklch(13% 0.02 216)";
  ctx.fillRect(0, 0, width, height);

  ctx.strokeStyle = "oklch(26% 0.018 210)";
  ctx.lineWidth = 1;

  for (let line = 0; line <= arena.size; line += 1) {
    const at = line * board.cell;

    ctx.beginPath();
    ctx.moveTo(board.left + at, board.top);
    ctx.lineTo(board.left + at, board.top + side);
    ctx.stroke();

    ctx.beginPath();
    ctx.moveTo(board.left, board.top + at);
    ctx.lineTo(board.left + side, board.top + at);
    ctx.stroke();
  }

  const food = cellCenter(board, arena.food);

  ctx.fillStyle = "oklch(70% 0.16 14)";
  ctx.beginPath();
  ctx.arc(food.x, food.y, board.cell * 0.29, 0, Math.PI * 2);
  ctx.fill();

  // tail first, so the brightest cell is the head: newest is brightest
  for (let at = arena.body.length - 1; at >= 0; at -= 1) {
    const cell = arena.body[at];

    if (cell === undefined) continue;

    ctx.fillStyle = at === 0 ? "oklch(86% 0.145 84)" : bodyShade(at / arena.body.length);

    const box = cellBox(board, cell);

    ctx.fillRect(box.x + 2, box.y + 2, board.cell - 4, board.cell - 4);
  }

  if (input.heading !== null)
    drawHeading(ctx, cellCenter(board, head(arena)), board.cell, input.heading);

  if (input.refused !== null) drawRefused(ctx, cellBox(board, input.refused), board.cell);
}

function bodyShade(howFar: number): string {
  const lightness = 76 - howFar * 22;
  const chroma = 0.115 - howFar * 0.05;

  return `oklch(${lightness.toFixed(1)}% ${chroma.toFixed(3)} 80)`;
}

function cellBox(board: FieldGeometry, cell: Point): FieldBox {
  return { x: board.left + cell.x * board.cell, y: board.top + cell.y * board.cell };
}

function cellCenter(board: FieldGeometry, cell: Point): FieldBox {
  return {
    x: board.left + (cell.x + 0.5) * board.cell,
    y: board.top + (cell.y + 0.5) * board.cell,
  };
}

function drawHeading(
  ctx: CanvasRenderingContext2D,
  center: FieldBox,
  cell: number,
  heading: Direction,
): void {
  const along = STEPS[heading];
  const across = { x: along.y, y: -along.x };
  const tipX = center.x + along.x * cell * 0.78;
  const tipY = center.y + along.y * cell * 0.78;
  const back = cell * 0.3;

  ctx.strokeStyle = "oklch(82% 0.135 82 / 0.75)";
  ctx.lineWidth = 2.5;
  ctx.beginPath();
  ctx.moveTo(center.x + along.x * cell * 0.2, center.y + along.y * cell * 0.2);
  ctx.lineTo(tipX, tipY);
  ctx.stroke();

  // the barb: two strokes back from the tip, one on each side of the heading
  ctx.beginPath();
  ctx.moveTo(tipX - along.x * back + across.x * back, tipY - along.y * back + across.y * back);
  ctx.lineTo(tipX, tipY);
  ctx.lineTo(tipX - along.x * back - across.x * back, tipY - along.y * back - across.y * back);
  ctx.stroke();
}

function drawRefused(ctx: CanvasRenderingContext2D, box: FieldBox, cell: number): void {
  ctx.strokeStyle = "oklch(64% 0.16 32 / 0.95)";
  ctx.lineWidth = 1.6;
  ctx.beginPath();
  ctx.moveTo(box.x + 4, box.y + 4);
  ctx.lineTo(box.x + cell - 4, box.y + cell - 4);
  ctx.moveTo(box.x + cell - 4, box.y + 4);
  ctx.lineTo(box.x + 4, box.y + cell - 4);
  ctx.stroke();
}
